import { useTranslation } from "react-i18next";

import { cn } from "@/shared/lib/utils";
import type { PosterAssetDto } from "../../../api/posterService";

/**
 * v2.9 版本切换器。
 *
 * 给定同 task 下所有 asset(含历史版本),按 parent_asset_id 链推算"原图 → v2 → v3 ..."顺序,
 * 用 radio button 切换。
 */
export interface VersionSwitcherProps {
  /** 当前 task 下,跟选中 asset 处于同一血缘链的 assets(含原图与所有重画版本)。 */
  versions: PosterAssetDto[];
  selectedId: number;
  onSelect: (id: number) => void;
}

export function VersionSwitcher({ versions, selectedId, onSelect }: VersionSwitcherProps) {
  const { t } = useTranslation("poster");
  if (versions.length <= 1) return null;
  // 标签按时间升序定义:id 升序即时间升序(雪花 id 递增),id 最小 = 原图,之后 v2/v3…
  const labeled = [...versions]
    .sort((a, b) => a.id - b.id)
    .map((v, i) => ({ asset: v, label: i === 0 ? t("editor.origin") : `v${i + 1}` }));
  // 2026-05-26 — 展示按时间降序(最新版本排最前),跟 grid「新图排前面」一致;原图落到最右。
  const display = [...labeled].reverse();
  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-lg border bg-muted/20 p-2">
      <span className="text-2xs text-muted-foreground">{t("editor.version")}</span>
      {display.map(({ asset: v, label }) => {
        const isCurrent = v.id === selectedId;
        return (
          <button
            key={v.id}
            type="button"
            onClick={() => onSelect(v.id)}
            className={cn(
              "rounded-md border px-2 py-0.5 text-xs transition-colors",
              isCurrent
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border hover:border-primary/50"
            )}
            title={label}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
